import React from 'react';

/**
 * Ilustraciones.js — los dibujos de las pantallas vacías.
 *
 * Una pantalla sin nada que enseñar es la que más se mira: alguien entró a su
 * reunión esperando el acta y se encontró dos renglones de texto gris. El
 * dibujo no añade información —la frase sigue debajo y es la que explica qué
 * pasó— pero cambia el tono: se lee como "aquí no había nada" y no como "algo
 * se rompió".
 *
 * Van en SVG y dibujados a mano, no como imagen:
 *
 *  · pesan unos pocos kilobytes y no son otra petición de red que puede fallar
 *    justo cuando la pantalla ya está fallando;
 *  · escalan a cualquier tamaño sin verse borrosos;
 *  · no traen licencia de terceros detrás.
 *
 * Los colores son fijos a propósito. Son ilustraciones planas, del mismo estilo
 * que se usa en el resto del producto, y su gracia está en esa paleta: el
 * amarillo de la carpeta y el rojo del aspa se reconocen antes de leer nada.
 * Seguirlos del tema los volvería manchas del color del fondo. Es la misma
 * decisión que ya se tomó con el acta, que se pinta sobre papel blanco.
 */

const TINTA = '#1f2937';
const PAPEL = '#f1f5f9';
const BORDE_PAPEL = '#cbd5e1';

/**
 * No se encontró lo que se buscaba.
 *
 * Una carpeta abierta, la hoja con cara de circunstancias asomando y la lupa
 * con el aspa encima: se buscó, se miró dentro, no estaba.
 */
export const NoSeEncontro = ({ ancho = 240, style }) => (
  <svg
    width={ancho} viewBox="0 0 320 250" fill="none" role="img"
    aria-label="No se encontró el archivo" style={style}
  >
    {/* La hoja, asomando por detrás de la carpeta */}
    <rect x="150" y="14" width="112" height="98" rx="6" fill={PAPEL} stroke={BORDE_PAPEL} strokeWidth="3" />
    <circle cx="183" cy="52" r="6.5" fill={TINTA} />
    <circle cx="229" cy="52" r="6.5" fill={TINTA} />
    <path d="M182 86q24-24 48 0" stroke={TINTA} strokeWidth="6" strokeLinecap="round" />

    {/* La carpeta: primero el lomo, luego la tapa de delante */}
    <path
      d="M136 84h40l13 15h89q9 0 9 9v88q0 9-9 9H136q-9 0-9-9V93q0-9 9-9z"
      fill="#fcd34d"
    />
    <path
      d="M124 116h172q7 0 6 8l-10 79q-1 8-9 8H137q-8 0-9-8l-10-79q-1-8 6-8z"
      fill="#f5b301"
    />

    {/* La lupa */}
    <line x1="252" y1="192" x2="288" y2="228" stroke={TINTA} strokeWidth="13" strokeLinecap="round" />
    <circle cx="226" cy="166" r="42" fill="#ffffff" />
    <circle cx="226" cy="166" r="42" stroke={TINTA} strokeWidth="7" />
    <path
      d="M210 150l32 32M242 150l-32 32"
      stroke="#ef4444" strokeWidth="9" strokeLinecap="round"
    />

    {/* Quien buscaba: sentado, con el portátil y una duda encima */}
    <circle cx="36" cy="62" r="16" fill="#ffffff" stroke={BORDE_PAPEL} strokeWidth="2.5" />
    <text
      x="36" y="69" textAnchor="middle" fill={TINTA}
      fontSize="19" fontWeight="700" fontFamily="Segoe UI, sans-serif"
    >?</text>
    <circle cx="20" cy="86" r="5" fill="#ffffff" stroke={BORDE_PAPEL} strokeWidth="2" />
    <circle cx="14" cy="97" r="3" fill="#ffffff" stroke={BORDE_PAPEL} strokeWidth="1.5" />

    <path d="M48 124q-3-20 16-20t16 20q-16 7-32 0z" fill={TINTA} />
    <circle cx="64" cy="132" r="17" fill="#f8c9a4" />
    <path d="M40 201q0-46 24-46t24 46z" fill="#3b82f6" />
    <path d="M26 200h76q7 0 7 8t-7 8H26q-7 0-7-8t7-8z" fill={TINTA} />
    <rect x="36" y="180" width="62" height="10" rx="4" fill="#64748b" />
    <path d="M47 152h40q4 0 4 4v22H43v-22q0-4 4-4z" fill={PAPEL} stroke="#64748b" strokeWidth="3" />
  </svg>
);

/**
 * Está tardando, pero está pasando algo.
 *
 * Una línea de texto gris a la izquierda de una pantalla vacía es ambigua: no
 * se distingue de un error que dejó el mensaje a medias. Un aro girando sobre
 * la hoja dice lo mismo, en el centro del hueco que va a ocuparse, y se ve sin
 * leerlo.
 *
 * Aquí sí se pasan los colores del tema, porque no es una ilustración: es un
 * indicador, y tiene que pertenecer a la pantalla donde está.
 */
export const Leyendo = ({
  texto = 'Leyendo reuniones…',
  color = '#94a3b8',
  acento = '#3b82f6',
  style
}) => (
  <div
    role="status"
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 13, padding: '40px 0 46px', color, ...style
    }}
  >
    <svg width="58" height="58" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      {/* La hoja que se está trayendo */}
      <rect x="16" y="13" width="16" height="22" rx="2.5" stroke={color} strokeWidth="2" opacity=".75" />
      <path
        d="M20 19h8M20 24h8M20 29h5"
        stroke={color} strokeWidth="2" strokeLinecap="round" opacity=".75"
      />
      {/* El aro: la pista quieta y el trazo que gira encima */}
      <circle cx="24" cy="24" r="21" stroke={color} strokeWidth="2.5" opacity=".18" />
      <path d="M24 3a21 21 0 0 1 21 21" stroke={acento} strokeWidth="2.5" strokeLinecap="round">
        <animateTransform
          attributeName="transform" type="rotate"
          from="0 24 24" to="360 24 24" dur="1s" repeatCount="indefinite"
        />
      </path>
    </svg>
    <span style={{ fontSize: 13.5 }}>{texto}</span>
  </div>
);

export default NoSeEncontro;
